import { isDefined } from "@clipboard-health/util-ts";
import { ActionType, onLoggedOut } from "@src/app/store/session";
import {
  clearEmbeddedAuth,
  storeEmbeddedAuth,
  waitForTokenRestoration,
} from "@src/appV2/EmbeddedMode/parentTokenRelay";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { invalidateGetWorkerQuery } from "@src/appV2/Worker/api/useGetWorker";
import type { CognitoUser } from "@src/lib/cognito/src/lib/amplifyAuth";
import { getCognitoLocalStorageState } from "@src/lib/cognito/src/lib/cognitoLocalStorageInspector";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

export function useIsAuthenticated(): boolean {
  const dispatch = useDispatch();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const queryClient = useQueryClient();

  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    let isCancelled = false;

    const cognitoAuth = getCognitoSingleton();

    logEvent(APP_V2_APP_EVENTS.COGNITO_STARTUP_LOCALSTORAGE_STATE, getCognitoLocalStorageState());

    const handleCognitoTokenChanged = async (
      cognitoUser: CognitoUser | undefined
    ): Promise<void> => {
      if (isCancelled) {
        return;
      }

      try {
        // Wait for token restoration from parent window (Safari ITP workaround).
        await waitForTokenRestoration();

        if (isDefined(cognitoUser)) {
          if (cognitoUser.cbhUserId) {
            logEvent(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORED, {
              uid: cognitoUser.uid,
              cbhUserId: cognitoUser.cbhUserId,
            });

            // Clear any stale isSignup flag carried over from a prior
            // onboarding session in the same tab (e.g. in-app reactivation
            // completion). The else-branch below sets isSignup=true when
            // cbhUserId is missing; nothing else clears it once present.
            dispatch({
              type: ActionType.SET_IS_SIGNUP,
              data: { isSignUp: false },
            });
          } else {
            // Mid-onboarding user — set Redux state for onboarding flow
            logEvent(APP_V2_APP_EVENTS.COGNITO_ONBOARDING_SESSION_RESTORED, {
              uid: cognitoUser.uid,
              phoneNumber: cognitoUser.phoneNumber,
            });

            dispatch({
              type: ActionType.SET_IS_SIGNUP,
              data: { isSignUp: true },
            });

            dispatch({
              type: ActionType.LOGGED_IN,
              data: {
                profile: { phone: cognitoUser.phoneNumber },
              },
            });
          }

          setAuthenticated(true);

          // Store auth in parent window for embedded mode (Safari ITP workaround)
          storeEmbeddedAuth();

          await invalidateGetWorkerQuery(queryClient);
        } else {
          // Cognito user is no longer available — log out
          logEvent(APP_V2_APP_EVENTS.COGNITO_USER_SIGNED_OUT);
          setAuthenticated(false);

          await queryClient.cancelQueries([GET_WORKER_QUERY_KEY]);
          queryClient.removeQueries([GET_WORKER_QUERY_KEY]);

          // Clear auth from parent window for embedded mode (Safari ITP workaround)
          clearEmbeddedAuth();

          onLoggedOut(dispatch);
        }
      } catch (error) {
        logError(APP_V2_APP_EVENTS.COGNITO_SESSION_RESTORE_ERROR, {
          error,
          metadata: { context: "handleCognitoTokenChanged" },
        });
      }
    };

    const unsubscribeCognito = cognitoAuth.onIdTokenChanged(handleCognitoTokenChanged, (error) => {
      logError(APP_V2_APP_EVENTS.COGNITO_AUTH_LISTENER_ERROR, {
        error,
        metadata: { source: "useIsAuthenticated" },
      });
    });

    return () => {
      isCancelled = true;
      unsubscribeCognito();
    };
    // Single registration — refs handle changing values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isAuthenticated;
}
